import React, { useEffect, useMemo, useState } from 'react';
import { Wallet, Download, ChevronLeft, ChevronRight, Loader2, MapPin, Zap } from 'lucide-react';
import { driverApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

/** Snap a JS Date to the Monday 00:00 of its ISO week (local). */
const isoMonday = (d) => {
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day);
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  m.setHours(0, 0, 0, 0);
  return m;
};

const fmtDate = (d) => d.toISOString().slice(0, 10);
const fmtRange = (start) => {
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const opt = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(undefined, opt)} – ${end.toLocaleDateString(undefined, { ...opt, year: 'numeric' })}`;
};

const StatCard = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const TIER_COLORS = {
  bronze: 'bg-orange-100 text-orange-800',
  silver: 'bg-slate-200 text-slate-800',
  gold: 'bg-amber-100 text-amber-800',
  platinum: 'bg-indigo-100 text-indigo-800',
  super: 'bg-fuchsia-100 text-fuchsia-800',
};

const DriverEarningsPage = () => {
  const [weekStart, setWeekStart] = useState(() => isoMonday(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const weekOf = useMemo(() => fmtDate(weekStart), [weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await driverApi.weeklyEarnings(weekOf);
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load earnings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekOf]);

  const shiftWeek = (delta) => {
    const n = new Date(weekStart); n.setDate(weekStart.getDate() + delta * 7);
    setWeekStart(isoMonday(n));
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const blob = await driverApi.weeklyEarningsCsv(weekOf);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipitnow-earnings-${weekOf}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Download failed'); }
    finally { setDownloading(false); }
  };

  const tier = data?.driver?.tier || 'bronze';
  const tierClass = TIER_COLORS[tier] || TIER_COLORS.bronze;
  const isCurrentWeek = fmtDate(isoMonday(new Date())) === weekOf;

  return (
    <div className="max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Wallet className="h-6 w-6 text-emerald-600" /> Earnings
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Weekly payout breakdown by order and tier. Export to CSV any time.</p>
        </div>
        <Button
          onClick={downloadCsv}
          disabled={downloading || loading || !data?.rows?.length}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="driver-earnings-download-csv"
        >
          {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
          Download CSV
        </Button>
      </div>

      {/* Week picker */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-3 flex items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="driver-earnings-prev-week">
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <div className="text-center">
          <div className="text-sm font-semibold text-slate-900" data-testid="driver-earnings-week-range">
            {fmtRange(weekStart)}
          </div>
          {isCurrentWeek && <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">This week</div>}
        </div>
        <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} disabled={isCurrentWeek} data-testid="driver-earnings-next-week">
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Driver + totals */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="driver-earnings-tier-card">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Tier</div>
          <div className="mt-1 flex items-center gap-2">
            <Badge className={`${tierClass} border-0 hover:opacity-100 capitalize text-sm px-2 py-0.5`}>{tier}</Badge>
            <span className="text-xs text-slate-500">· {data?.driver?.payout_share_pct ?? '—'}% share</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-2 truncate">{data?.driver?.name || '—'}</div>
        </div>
        <StatCard label="Deliveries" value={data?.totals?.deliveries ?? '—'} testid="driver-earnings-deliveries-card" />
        <StatCard label="Gross delivery fees" value={loading ? '…' : `$${(data?.totals?.gross_delivery_fees ?? 0).toFixed(2)}`} sub="Before your share" testid="driver-earnings-gross-card" />
        <StatCard label="Your earnings" value={loading ? '…' : `$${(data?.totals?.earnings ?? 0).toFixed(2)}`} sub={`CAD $${(data?.totals?.earnings_cad ?? 0).toFixed(2)}`} testid="driver-earnings-net-card" />
      </div>

      {/* Rows */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="text-center py-14 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading…</div>
        ) : (data?.rows?.length || 0) === 0 ? (
          <div className="text-center py-14 text-slate-500" data-testid="driver-earnings-empty">
            No deliveries in this week. Try picking a previous week or go online to grab some orders.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Order</th>
                  <th className="text-left font-medium px-4 py-3">Vendor</th>
                  <th className="text-right font-medium px-4 py-3">Distance</th>
                  <th className="text-right font-medium px-4 py-3">Delivery fee</th>
                  <th className="text-right font-medium px-4 py-3">Your cut</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-left font-medium px-4 py-3">Delivered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => (
                  <tr key={r.order_id} className="hover:bg-slate-50" data-testid={`driver-earnings-row-${r.order_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {r.order_id}
                        {r.priority && <Zap className="h-3 w-3 text-emerald-600" title="Priority delivery" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-900">{r.vendor_name}</td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">
                      {r.distance_km ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-slate-400" />{r.distance_km} km</span> : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">${r.delivery_fee.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700 tabular-nums" data-testid={`driver-earnings-cut-${r.order_id}`}>
                      ${r.earning.toFixed(2)}
                      {r.currency !== 'CAD' && <div className="text-[10px] text-slate-400 font-normal">CAD ${r.earning_cad.toFixed(2)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${r.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'} border-0 capitalize`}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">
                      {r.delivered_at ? new Date(r.delivered_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr className="font-semibold text-slate-900">
                  <td className="px-4 py-3" colSpan="3">Weekly total</td>
                  <td className="px-4 py-3 text-right tabular-nums">${(data.totals.gross_delivery_fees).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 tabular-nums" data-testid="driver-earnings-total-cut">${(data.totals.earnings).toFixed(2)}</td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Payouts show as <em>pending</em> until an admin releases them. Your tier
        share is applied automatically — level up by delivering more and keeping
        a 4.7★ rating.
      </p>
    </div>
  );
};

export default DriverEarningsPage;
